import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Divider,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Modal,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  LocalOffer, 
  Code, 
  CalendarToday, 
  CheckCircle, 
  Cancel,
  Info,
  Edit,
  Delete
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import apiService from '../../services/apiService';
import { useToast } from '../common/ToastProvider';
import '../styles/ContentComponents.css';

function PromoCodes() {
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState(0);
  const [featureCodes, setFeatureCodes] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Subscription plans from API
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  
  // Common feature options
  const featureOptions = [
    'auto_integrate'
  ];
  const [editFeatureModalOpen, setEditFeatureModalOpen] = useState(false);
  const [editDiscountModalOpen, setEditDiscountModalOpen] = useState(false);
  const [editingFeatureCode, setEditingFeatureCode] = useState(null);
  const [editingDiscountCode, setEditingDiscountCode] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [createFeatureModalOpen, setCreateFeatureModalOpen] = useState(false);
  const [createDiscountModalOpen, setCreateDiscountModalOpen] = useState(false);
  const [createFeatureForm, setCreateFeatureForm] = useState({
    code: '',
    enable_features: [],
    valid_from: '',
    valid_until: ''
  });
  const [createDiscountForm, setCreateDiscountForm] = useState({
    code: '',
    subscriptions: [],
    valid_from: '',
    valid_until: ''
  });
  const [createLoading, setCreateLoading] = useState(false);
  
  // Delete confirmation modals
  const [deleteFeatureModalOpen, setDeleteFeatureModalOpen] = useState(false);
  const [deleteDiscountModalOpen, setDeleteDiscountModalOpen] = useState(false);
  const [deletingFeatureCode, setDeletingFeatureCode] = useState(null);
  const [deletingDiscountCode, setDeletingDiscountCode] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchAllCodes();
  }, []);

  const fetchAllCodes = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch feature codes, discount codes, and subscription plans in parallel
      const [featureResult, discountResult, subscriptionResult] = await Promise.all([
        apiService.fetchAllFeatureCodes(),
        apiService.fetchAllDiscountCodes(),
        apiService.fetchSubscriptionPlans()
      ]);
      
      if (featureResult.success) {
        setFeatureCodes(featureResult.data || []);
      } else {
        console.error('Failed to fetch feature codes:', featureResult.error);
      }
      
      if (discountResult.success) {
        setDiscountCodes(discountResult.data || []);
      } else {
        console.error('Failed to fetch discount codes:', discountResult.error);
      }
      
      if (subscriptionResult.success) {
        setSubscriptionPlans(subscriptionResult.data || []);
      } else {
        console.error('Failed to fetch subscription plans:', subscriptionResult.error);
      }
      
      // Set error if all failed
      if (!featureResult.success && !discountResult.success && !subscriptionResult.success) {
        setError('Failed to load promo codes data');
      }
    } catch (err) {
      setError('Failed to load promo codes data');
      console.error('Failed to fetch promo codes:', err);
    }
    
    setLoading(false);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getValidityStatus = (validFrom, validUntil) => {
    const now = new Date();
    const fromDate = validFrom ? new Date(validFrom) : null;
    const untilDate = validUntil ? new Date(validUntil) : null;
    
    if (fromDate && now < fromDate) {
      return { status: 'pending', label: 'Not Started', color: 'warning' };
    }
    
    if (untilDate && now > untilDate) {
      return { status: 'expired', label: 'Expired', color: 'error' };
    }
    
    return { status: 'active', label: 'Active', color: 'success' };
  };

  const formatFeatures = (features) => {
    if (!features || features.length === 0) return 'No features';
    return features.join(', ');
  };

  const formatSubscriptions = (subscriptions) => {
    if (!subscriptions || subscriptions.length === 0) return 'All plans';
    return subscriptions.join(', ');
  };

  const handleEditFeatureCode = (code) => {
    setEditingFeatureCode({
      ...code,
      valid_from: code.valid_from ? code.valid_from.split('T')[0] : '',
      valid_until: code.valid_until ? code.valid_until.split('T')[0] : '',
      enable_features: code.enable_features || []
    });
    setEditFeatureModalOpen(true);
  };

  const handleEditDiscountCode = (code) => {
    setEditingDiscountCode({
      ...code,
      valid_from: code.valid_from ? code.valid_from.split('T')[0] : '',
      valid_until: code.valid_until ? code.valid_until.split('T')[0] : '',
      subscriptions: code.subscriptions || []
    });
    setEditDiscountModalOpen(true);
  };

  const handleEditFeatureModalClose = () => {
    setEditFeatureModalOpen(false);
    setEditingFeatureCode(null);
  };

  const handleEditDiscountModalClose = () => {
    setEditDiscountModalOpen(false);
    setEditingDiscountCode(null);
  };

  const handleUpdateFeatureCode = async () => {
    if (!editingFeatureCode) return;

    setEditLoading(true);

    try {
      const updateData = {
        code: editingFeatureCode.code,
        enable_features: editingFeatureCode.enable_features,
        valid_from: editingFeatureCode.valid_from ? new Date(editingFeatureCode.valid_from).toISOString() : null,
        valid_until: editingFeatureCode.valid_until ? new Date(editingFeatureCode.valid_until).toISOString() : null
      };

      const result = await apiService.updateFeatureCode(editingFeatureCode.unique_id, updateData);

      if (result.success) {
        console.log('Feature code updated successfully:', result.data);
        // Refresh the data
        await fetchAllCodes();
        setEditFeatureModalOpen(false);
        setEditingFeatureCode(null);
        showSuccess('Feature code updated successfully!', 'Success');
      } else {
        console.error('Failed to update feature code:', result.error);
        showError(`Failed to update feature code: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error updating feature code:', error);
      showError('Failed to update feature code. Please try again.', 'Error');
    } finally {
      setEditLoading(false);
    }
  };

  const handleUpdateDiscountCode = async () => {
    if (!editingDiscountCode) return;

    setEditLoading(true);

    try {
      // Convert subscription objects to just IDs, or keep strings as they are
      const subscriptionIds = editingDiscountCode.subscriptions.map(sub => {
        if (typeof sub === 'string') return sub;
        return sub.id;
      });

      const updateData = {
        code: editingDiscountCode.code,
        subscriptions: subscriptionIds,
        valid_from: editingDiscountCode.valid_from ? new Date(editingDiscountCode.valid_from).toISOString() : null,
        valid_until: editingDiscountCode.valid_until ? new Date(editingDiscountCode.valid_until).toISOString() : null
      };

      const result = await apiService.updateDiscountCode(editingDiscountCode.unique_id, updateData);

      if (result.success) {
        console.log('Discount code updated successfully:', result.data);
        // Refresh the data
        await fetchAllCodes();
        setEditDiscountModalOpen(false);
        setEditingDiscountCode(null);
        showSuccess('Discount code updated successfully!', 'Success');
      } else {
        console.error('Failed to update discount code:', result.error);
        showError(`Failed to update discount code: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error updating discount code:', error);
      showError('Failed to update discount code. Please try again.', 'Error');
    } finally {
      setEditLoading(false);
    }
  };

  const handleCreateFeatureClick = () => {
    setCreateFeatureModalOpen(true);
  };

  const handleCreateDiscountClick = () => {
    setCreateDiscountModalOpen(true);
  };

  const handleCreateFeatureModalClose = () => {
    setCreateFeatureModalOpen(false);
    setCreateFeatureForm({
      code: '',
      enable_features: [],
      valid_from: '',
      valid_until: ''
    });
  };

  const handleCreateDiscountModalClose = () => {
    setCreateDiscountModalOpen(false);
    setCreateDiscountForm({
      code: '',
      subscriptions: [],
      valid_from: '',
      valid_until: ''
    });
  };

  const handleCreateFeatureCode = async () => {
    if (!createFeatureForm.code.trim() || !createFeatureForm.valid_from) {
      showError('Please fill in all required fields (Code and Valid From are required)', 'Validation Error');
      return;
    }

    setCreateLoading(true);

    try {
      const createData = {
        code: createFeatureForm.code.trim(),
        enable_features: createFeatureForm.enable_features,
        valid_from: new Date(createFeatureForm.valid_from).toISOString(),
        valid_until: createFeatureForm.valid_until ? new Date(createFeatureForm.valid_until).toISOString() : null
      };

      const result = await apiService.createFeatureCode(createData);

      if (result.success) {
        console.log('Feature code created successfully:', result.data);
        // Refresh the data
        await fetchAllCodes();
        setCreateFeatureModalOpen(false);
        setCreateFeatureForm({
          code: '',
          enable_features: [],
          valid_from: '',
          valid_until: ''
        });
        showSuccess('Feature code created successfully!', 'Success');
      } else {
        console.error('Failed to create feature code:', result.error);
        showError(`Failed to create feature code: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error creating feature code:', error);
      showError('Failed to create feature code. Please try again.', 'Error');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCreateDiscountCode = async () => {
    if (!createDiscountForm.code.trim() || !createDiscountForm.valid_from) {
      showError('Please fill in all required fields (Code and Valid From are required)', 'Validation Error');
      return;
    }

    setCreateLoading(true);

    try {
      // Convert subscription objects to just IDs, or keep strings as they are
      const subscriptionIds = createDiscountForm.subscriptions.map(sub => {
        if (typeof sub === 'string') return sub;
        return sub.id;
      });

      const createData = {
        code: createDiscountForm.code.trim(),
        subscriptions: subscriptionIds,
        valid_from: new Date(createDiscountForm.valid_from).toISOString(),
        valid_until: createDiscountForm.valid_until ? new Date(createDiscountForm.valid_until).toISOString() : null
      };

      const result = await apiService.createDiscountCode(createData);

      if (result.success) {
        console.log('Discount code created successfully:', result.data);
        // Refresh the data
        await fetchAllCodes();
        setCreateDiscountModalOpen(false);
        setCreateDiscountForm({
          code: '',
          subscriptions: [],
          valid_from: '',
          valid_until: ''
        });
        showSuccess('Discount code created successfully!', 'Success');
      } else {
        console.error('Failed to create discount code:', result.error);
        showError(`Failed to create discount code: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error creating discount code:', error);
      showError('Failed to create discount code. Please try again.', 'Error');
    } finally {
      setCreateLoading(false);
    }
  };

  // Delete handlers
  const handleDeleteFeatureCode = (code) => {
    setDeletingFeatureCode(code);
    setDeleteFeatureModalOpen(true);
  };

  const handleDeleteDiscountCode = (code) => {
    setDeletingDiscountCode(code);
    setDeleteDiscountModalOpen(true);
  };

  const handleDeleteFeatureModalClose = () => {
    setDeleteFeatureModalOpen(false);
    setDeletingFeatureCode(null);
  };

  const handleDeleteDiscountModalClose = () => {
    setDeleteDiscountModalOpen(false);
    setDeletingDiscountCode(null);
  };

  const handleConfirmDeleteFeatureCode = async () => {
    if (!deletingFeatureCode) return;

    setDeleteLoading(true);

    try {
      const result = await apiService.deleteFeatureCode(deletingFeatureCode.unique_id);

      if (result.success) {
        console.log('Feature code deleted successfully:', result.data);
        // Refresh the data
        await fetchAllCodes();
        setDeleteFeatureModalOpen(false);
        setDeletingFeatureCode(null);
        showSuccess('Feature code deleted successfully!', 'Success');
      } else {
        console.error('Failed to delete feature code:', result.error);
        showError(`Failed to delete feature code: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error deleting feature code:', error);
      showError('Failed to delete feature code. Please try again.', 'Error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleConfirmDeleteDiscountCode = async () => {
    if (!deletingDiscountCode) return;

    setDeleteLoading(true);

    try {
      const result = await apiService.deleteDiscountCode(deletingDiscountCode.unique_id);

      if (result.success) {
        console.log('Discount code deleted successfully:', result.data);
        // Refresh the data
        await fetchAllCodes();
        setDeleteDiscountModalOpen(false);
        setDeletingDiscountCode(null);
        showSuccess('Discount code deleted successfully!', 'Success');
      } else {
        console.error('Failed to delete discount code:', result.error);
        showError(`Failed to delete discount code: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error deleting discount code:', error);
      showError('Failed to delete discount code. Please try again.', 'Error');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        width: '100%', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        width: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        gap: 3,
        px: 2
      }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={fetchAllCodes}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      gap: 3,
      px: 2
    }}>
      {/* Tabs Card - Maximum Width */}
      <Card sx={{ width: '100%', maxWidth: '1400px' }}>
        <CardContent sx={{ p: 0 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              px: 3,
              pt: 2
            }}
          >
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Code fontSize="small" />
                  <Typography>Feature Codes ({featureCodes.length})</Typography>
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalOffer fontSize="small" />
                  <Typography>Discount Codes ({discountCodes.length})</Typography>
                </Box>
              } 
            />
          </Tabs>
        </CardContent>
      </Card>

      {/* Feature Codes Tab */}
      {activeTab === 0 && (
        <Card sx={{ width: '100%', maxWidth: '1400px', minHeight: '600px' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Code sx={{ fontSize: 28, color: 'primary.main' }} />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Feature Codes
                </Typography>
                <Chip 
                  label={`${featureCodes.length} codes`}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              </Box>
              <IconButton
                size="small"
                onClick={handleCreateFeatureClick}
                sx={{ 
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'white',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>

            {featureCodes.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 6,
                px: 3
              }}>
                <Code sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  No Feature Codes Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No feature codes have been created yet.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Features</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Valid From</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Valid Until</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {featureCodes.map((code) => {
                      const validity = getValidityStatus(code.valid_from, code.valid_until);
                      return (
                        <TableRow 
                          key={code.unique_id}
                          sx={{ 
                            '&:hover': { 
                              backgroundColor: 'grey.50',
                              transition: 'background-color 0.2s ease'
                            }
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                {code.code}
                              </Typography>
                              <Tooltip title="Feature Code">
                                <Code fontSize="small" color="primary" />
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 200 }}>
                              {formatFeatures(code.enable_features)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarToday fontSize="small" color="action" />
                              <Typography variant="body2">
                                {formatDate(code.valid_from)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarToday fontSize="small" color="action" />
                              <Typography variant="body2">
                                {formatDate(code.valid_until)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={validity.label}
                              color={validity.color}
                              size="small"
                              icon={validity.status === 'active' ? <CheckCircle /> : <Cancel />}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(code.created_at)}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditFeatureCode(code)}
                                  sx={{ 
                                    color: 'primary.main',
                                    p: 0.5,
                                    '&:hover': {
                                      backgroundColor: 'primary.light',
                                      color: 'white',
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease-in-out'
                                  }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteFeatureCode(code)}
                                  sx={{ 
                                    color: 'error.main',
                                    p: 0.5,
                                    '&:hover': {
                                      backgroundColor: 'error.light',
                                      color: 'white',
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease-in-out'
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Discount Codes Tab */}
      {activeTab === 1 && (
        <Card sx={{ width: '100%', maxWidth: '1400px', minHeight: '600px' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LocalOffer sx={{ fontSize: 28, color: 'primary.main' }} />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Discount Codes
                </Typography>
                <Chip 
                  label={`${discountCodes.length} codes`}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              </Box>
              <IconButton
                size="small"
                onClick={handleCreateDiscountClick}
                sx={{ 
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'white',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>

            {discountCodes.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 6,
                px: 3
              }}>
                <LocalOffer sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  No Discount Codes Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No discount codes have been created yet.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Subscriptions</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Valid From</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Valid Until</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {discountCodes.map((code) => {
                      const validity = getValidityStatus(code.valid_from, code.valid_until);
                      return (
                        <TableRow 
                          key={code.unique_id}
                          sx={{ 
                            '&:hover': { 
                              backgroundColor: 'grey.50',
                              transition: 'background-color 0.2s ease'
                            }
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                {code.code}
                              </Typography>
                              <Tooltip title="Discount Code">
                                <LocalOffer fontSize="small" color="primary" />
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 200 }}>
                              {formatSubscriptions(code.subscriptions)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarToday fontSize="small" color="action" />
                              <Typography variant="body2">
                                {formatDate(code.valid_from)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarToday fontSize="small" color="action" />
                              <Typography variant="body2">
                                {formatDate(code.valid_until)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={validity.label}
                              color={validity.color}
                              size="small"
                              icon={validity.status === 'active' ? <CheckCircle /> : <Cancel />}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(code.create_date)}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditDiscountCode(code)}
                                  sx={{ 
                                    color: 'primary.main',
                                    p: 0.5,
                                    '&:hover': {
                                      backgroundColor: 'primary.light',
                                      color: 'white',
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease-in-out'
                                  }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteDiscountCode(code)}
                                  sx={{ 
                                    color: 'error.main',
                                    p: 0.5,
                                    '&:hover': {
                                      backgroundColor: 'error.light',
                                      color: 'white',
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease-in-out'
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Feature Code Modal */}
      <Modal
        open={editFeatureModalOpen}
        onClose={handleEditFeatureModalClose}
        aria-labelledby="edit-feature-code-modal"
        aria-describedby="edit-feature-code-form"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 4,
        }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            Edit Feature Code
          </Typography>
          
          {editingFeatureCode && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Code"
                value={editingFeatureCode.code}
                onChange={(e) => setEditingFeatureCode(prev => ({ ...prev, code: e.target.value }))}
                disabled={editLoading}
                sx={{ mb: 3 }}
              />

              <Autocomplete
                multiple
                freeSolo
                options={featureOptions}
                value={editingFeatureCode.enable_features}
                onChange={(event, newValue) => {
                  setEditingFeatureCode(prev => ({ 
                    ...prev, 
                    enable_features: newValue
                  }));
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                      color="primary"
                      size="small"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Features"
                    placeholder="Select or type feature names"
                    disabled={editLoading}
                  />
                )}
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                label="Valid From"
                type="date"
                value={editingFeatureCode.valid_from}
                onChange={(e) => setEditingFeatureCode(prev => ({ ...prev, valid_from: e.target.value }))}
                disabled={editLoading}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                label="Valid Until"
                type="date"
                value={editingFeatureCode.valid_until}
                onChange={(e) => setEditingFeatureCode(prev => ({ ...prev, valid_until: e.target.value }))}
                disabled={editLoading}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 3 }}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button 
              variant="outlined" 
              onClick={handleEditFeatureModalClose}
              disabled={editLoading}
              sx={{ px: 3, py: 1 }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleUpdateFeatureCode}
              disabled={editLoading || !editingFeatureCode?.code?.trim()}
              sx={{ px: 3, py: 1, minWidth: 100 }}
            >
              {editLoading ? 'Updating...' : 'Update'}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Edit Discount Code Modal */}
      <Modal
        open={editDiscountModalOpen}
        onClose={handleEditDiscountModalClose}
        aria-labelledby="edit-discount-code-modal"
        aria-describedby="edit-discount-code-form"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 4,
        }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            Edit Discount Code
          </Typography>
          
          {editingDiscountCode && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Code"
                value={editingDiscountCode.code}
                onChange={(e) => setEditingDiscountCode(prev => ({ ...prev, code: e.target.value }))}
                disabled={editLoading}
                sx={{ mb: 3 }}
              />

              <Autocomplete
                multiple
                freeSolo
                options={subscriptionPlans.map(plan => ({ name: plan.name, id: plan.unique_id }))}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return `${option.name} (ID: ${option.id})`;
                }}
                isOptionEqualToValue={(option, value) => {
                  if (typeof option === 'string' && typeof value === 'string') return option === value;
                  if (typeof option === 'object' && typeof value === 'object') return option.id === value.id;
                  return false;
                }}
                value={editingDiscountCode.subscriptions}
                onChange={(event, newValue) => {
                  setEditingDiscountCode(prev => ({ 
                    ...prev, 
                    subscriptions: newValue
                  }));
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={typeof option === 'string' ? option : option.name}
                      {...getTagProps({ index })}
                      color="primary"
                      size="small"
                    />
                  ))
                }
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ID: {option.id}
                    </Typography>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Subscriptions"
                    placeholder="Select or type subscription plans"
                    disabled={editLoading}
                  />
                )}
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                label="Valid From"
                type="date"
                value={editingDiscountCode.valid_from}
                onChange={(e) => setEditingDiscountCode(prev => ({ ...prev, valid_from: e.target.value }))}
                disabled={editLoading}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                label="Valid Until"
                type="date"
                value={editingDiscountCode.valid_until}
                onChange={(e) => setEditingDiscountCode(prev => ({ ...prev, valid_until: e.target.value }))}
                disabled={editLoading}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 3 }}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button 
              variant="outlined" 
              onClick={handleEditDiscountModalClose}
              disabled={editLoading}
              sx={{ px: 3, py: 1 }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleUpdateDiscountCode}
              disabled={editLoading || !editingDiscountCode?.code?.trim()}
              sx={{ px: 3, py: 1, minWidth: 100 }}
            >
              {editLoading ? 'Updating...' : 'Update'}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Create Feature Code Modal */}
      <Modal
        open={createFeatureModalOpen}
        onClose={handleCreateFeatureModalClose}
        aria-labelledby="create-feature-code-modal"
        aria-describedby="create-feature-code-form"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 4,
        }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            Create Feature Code
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Code *"
              value={createFeatureForm.code}
              onChange={(e) => setCreateFeatureForm(prev => ({ ...prev, code: e.target.value }))}
              placeholder="Enter feature code"
              disabled={createLoading}
              sx={{ mb: 3 }}
              required
            />

            <Autocomplete
              multiple
              freeSolo
              options={featureOptions}
              value={createFeatureForm.enable_features}
              onChange={(event, newValue) => {
                setCreateFeatureForm(prev => ({ 
                  ...prev, 
                  enable_features: newValue
                }));
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                    color="primary"
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Features"
                  placeholder="Select or type feature names"
                  disabled={createLoading}
                />
              )}
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              label="Valid From *"
              type="date"
              value={createFeatureForm.valid_from}
              onChange={(e) => setCreateFeatureForm(prev => ({ ...prev, valid_from: e.target.value }))}
              disabled={createLoading}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 3 }}
              required
            />

            <TextField
              fullWidth
              label="Valid Until"
              type="date"
              value={createFeatureForm.valid_until}
              onChange={(e) => setCreateFeatureForm(prev => ({ ...prev, valid_until: e.target.value }))}
              disabled={createLoading}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 3 }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button 
              variant="outlined" 
              onClick={handleCreateFeatureModalClose}
              disabled={createLoading}
              sx={{ px: 3, py: 1 }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleCreateFeatureCode}
              disabled={createLoading || !createFeatureForm.code.trim() || !createFeatureForm.valid_from}
              sx={{ px: 3, py: 1, minWidth: 100 }}
            >
              {createLoading ? 'Creating...' : 'Create'}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Create Discount Code Modal */}
      <Modal
        open={createDiscountModalOpen}
        onClose={handleCreateDiscountModalClose}
        aria-labelledby="create-discount-code-modal"
        aria-describedby="create-discount-code-form"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 4,
        }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            Create Discount Code
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Code *"
              value={createDiscountForm.code}
              onChange={(e) => setCreateDiscountForm(prev => ({ ...prev, code: e.target.value }))}
              placeholder="Enter discount code"
              disabled={createLoading}
              sx={{ mb: 3 }}
              required
            />

            <Autocomplete
              multiple
              freeSolo
              options={subscriptionPlans.map(plan => ({ name: plan.name, id: plan.unique_id }))}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return `${option.name} (ID: ${option.id})`;
              }}
              isOptionEqualToValue={(option, value) => {
                if (typeof option === 'string' && typeof value === 'string') return option === value;
                if (typeof option === 'object' && typeof value === 'object') return option.id === value.id;
                return false;
              }}
              value={createDiscountForm.subscriptions}
              onChange={(event, newValue) => {
                setCreateDiscountForm(prev => ({ 
                  ...prev, 
                  subscriptions: newValue
                }));
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={typeof option === 'string' ? option : option.name}
                    {...getTagProps({ index })}
                    color="primary"
                    size="small"
                  />
                ))
              }
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {option.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    ID: {option.id}
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Subscriptions"
                  placeholder="Select or type subscription plans"
                  disabled={createLoading}
                />
              )}
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              label="Valid From *"
              type="date"
              value={createDiscountForm.valid_from}
              onChange={(e) => setCreateDiscountForm(prev => ({ ...prev, valid_from: e.target.value }))}
              disabled={createLoading}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 3 }}
              required
            />

            <TextField
              fullWidth
              label="Valid Until"
              type="date"
              value={createDiscountForm.valid_until}
              onChange={(e) => setCreateDiscountForm(prev => ({ ...prev, valid_until: e.target.value }))}
              disabled={createLoading}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 3 }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button 
              variant="outlined" 
              onClick={handleCreateDiscountModalClose}
              disabled={createLoading}
              sx={{ px: 3, py: 1 }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleCreateDiscountCode}
              disabled={createLoading || !createDiscountForm.code.trim() || !createDiscountForm.valid_from}
              sx={{ px: 3, py: 1, minWidth: 100 }}
            >
              {createLoading ? 'Creating...' : 'Create'}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Delete Feature Code Confirmation Modal */}
      <Dialog
        open={deleteFeatureModalOpen}
        onClose={handleDeleteFeatureModalClose}
        aria-labelledby="delete-feature-code-dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Delete color="error" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Delete Feature Code
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {deletingFeatureCode && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to delete the feature code <strong>"{deletingFeatureCode.code}"</strong>?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This action cannot be undone. The feature code will be permanently removed from the system.
              </Typography>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'grey.50', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.300'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                  Code Details:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Code:</strong> {deletingFeatureCode.code}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Features:</strong> {formatFeatures(deletingFeatureCode.enable_features)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Valid From:</strong> {formatDate(deletingFeatureCode.valid_from)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Valid Until:</strong> {formatDate(deletingFeatureCode.valid_until)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleDeleteFeatureModalClose}
            disabled={deleteLoading}
            sx={{ px: 3, py: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDeleteFeatureCode}
            disabled={deleteLoading}
            variant="contained"
            color="error"
            sx={{ px: 3, py: 1 }}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Discount Code Confirmation Modal */}
      <Dialog
        open={deleteDiscountModalOpen}
        onClose={handleDeleteDiscountModalClose}
        aria-labelledby="delete-discount-code-dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Delete color="error" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Delete Discount Code
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {deletingDiscountCode && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to delete the discount code <strong>"{deletingDiscountCode.code}"</strong>?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This action cannot be undone. The discount code will be permanently removed from the system.
              </Typography>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'grey.50', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.300'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                  Code Details:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Code:</strong> {deletingDiscountCode.code}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Subscriptions:</strong> {formatSubscriptions(deletingDiscountCode.subscriptions)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Valid From:</strong> {formatDate(deletingDiscountCode.valid_from)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Valid Until:</strong> {formatDate(deletingDiscountCode.valid_until)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleDeleteDiscountModalClose}
            disabled={deleteLoading}
            sx={{ px: 3, py: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDeleteDiscountCode}
            disabled={deleteLoading}
            variant="contained"
            color="error"
            sx={{ px: 3, py: 1 }}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PromoCodes; 